import React from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronRightIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import ctaBannerImage from '../assets/trading/cta-banner@2x.webp';
import appTradingImage from '../assets/trading/apptrading.png';
import exchangeScreenImage from '../assets/trading/exchange-screen@2x-1.webp';
import futuresImage from '../assets/trading/futures.webp';
import retirementImage from '../assets/trading/retirement.webp';
import businessPersonImage from '../assets/business-person-futuristic-business-environment.jpg';
import teamInvestorsImage from '../assets/business-team-singapore.jpg';

const ExchangePage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "What is the SEGWAY CAPITAL Exchange?",
      answer: "SEGWAY CAPITAL Exchange is a professional cryptocurrency trading platform that provides access to over 400+ crypto pairs using advanced order books and institutional-grade tools."
    },
    {
      question: "How do I exchange crypto?",
      answer: "Simply sign up for an account, deposit your crypto or fiat, and use our intuitive trading interface to buy and sell digital assets with competitive pricing and instant execution."
    },
    {
      question: "What crypto assets does the exchange support?",
      answer: "We support over 400+ cryptocurrency pairs including Bitcoin, Ethereum, and all major altcoins, with regular additions of new assets based on market demand."
    },
    {
      question: "What are the minimum and maximum trade limits?",
      answer: "Minimum trade amounts vary by asset, typically starting at $10. Maximum limits depend on your account tier and verification level, with institutional clients having higher limits."
    },
    {
      question: "Can I exchange crypto and fiat currency?",
      answer: "Yes, our platform supports both crypto-to-crypto and crypto-to-fiat trading pairs, allowing you to easily convert between digital and traditional currencies."
    },
    {
      question: "How does SEGWAY CAPITAL Exchange work?",
      answer: "Our exchange uses advanced order matching technology with deep liquidity from multiple leading exchanges, ensuring competitive prices and fast execution for all trades."
    }
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6"
              >
                Pick the right tool for your portfolio growth
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl text-gray-600 mb-8"
              >
                Exchange over 400+ crypto pairs using Royal Pro Order Books and advanced tools.
              </motion.p>
              <motion.button 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-800 transition-colors flex items-center"
              >
                Sign up
                <ChevronRightIcon className="w-5 h-5 ml-2" />
              </motion.button>
            </div>
            <div className="relative">
              <div className="relative z-10">
                <img src={exchangeScreenImage} alt="Exchange Platform" className="w-full max-w-sm mx-auto" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-green-100 to-blue-100 rounded-3xl -z-10 transform rotate-3 scale-105"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-16">
            The benefits of the SEGWAY CAPITAL Exchange
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "🔄",
                title: "Deep Liquidity",
                description: "Our connections with numerous leading exchanges ensure deep liquidity and competitive prices."
              },
              {
                icon: "💰",
                title: "Competitive Pricing",
                description: "Enjoy competitive pricing with minimal spreads."
              },
              {
                icon: "🎯",
                title: "Buy and sell at your target price",
                description: "Set your desired price and let our automated system execute your trades."
              },
              {
                icon: "📊",
                title: "Advanced Order Types",
                description: "Utilize advanced order types like limit, stop-limit, and OCO (One-Cancels-the-Other)."
              },
              {
                icon: "⚡",
                title: "Instant Execution",
                description: "Experience lightning-fast trade execution for seamless transactions."
              },
              {
                icon: "🎧",
                title: "24/7 Support",
                description: "Access dedicated customer support around the clock for any queries."
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-4"
              >
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl shadow-lg flex-shrink-0">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Price Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-16">
            Buy and sell at your target price
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: "🎯",
                title: "Set limit orders",
                description: "Place orders at your desired price and let the market come to you."
              },
              {
                icon: "📈",
                title: "View real-time market data",
                description: "Access live pricing, volume, and order book data for informed decisions."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-4"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-2xl flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leverage Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-16">
            Buy up to 3x more crypto
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: "🤝",
                title: "Leverage trading",
                description: "Trade with up to 3x leverage to amplify your potential returns."
              },
              {
                icon: "❌",
                title: "No margin calls",
                description: "Our smart liquidation system prevents unexpected margin calls."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-4"
              >
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl shadow-lg flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Private Clients Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
                Exclusive trading experience for private clients with over $500,000 in digital assets
              </h2>
              <div className="space-y-6 mb-8">
                {[
                  {
                    icon: "👤",
                    title: "Dedicated Account Manager",
                    description: "Receive personalized support and guidance from a dedicated account manager."
                  },
                  {
                    icon: "💱",
                    title: "OTC Trading",
                    description: "Access over-the-counter (OTC) trading for large block trades."
                  },
                  {
                    icon: "💎",
                    title: "Preferential Rates",
                    description: "Benefit from preferential trading rates and lower fees."
                  }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-xl flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="text-green-600 font-semibold hover:text-green-700 transition-colors flex items-center">
                Explore more
                <ChevronRightIcon className="w-5 h-5 ml-2" />
              </button>
            </div>
            <div>
              <img src={teamInvestorsImage} alt="Private Client" className="w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Business Strategy Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6">
                <span className="text-4xl">🏢</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Execute trades that match your business strategy
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Our institutional-grade platform provides robust tools and features for all your trading needs.
              </p>
              <button className="text-white font-semibold hover:text-gray-200 transition-colors flex items-center">
                Explore more
                <ChevronRightIcon className="w-5 h-5 ml-2" />
              </button>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 to-blue-600/20 rounded-3xl"></div>
              <div className="relative z-10 bg-white/10 backdrop-blur-sm rounded-3xl p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📊</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Institutional Tools</h3>
                  <p className="text-gray-300">Advanced trading infrastructure for professional investors</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Data Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
                All the important market data is in your hands
              </h2>
              <div className="space-y-6 mb-8">
                {[
                  {
                    icon: "📊",
                    title: "Receive real-time market data",
                    description: "Access live pricing, volume, and market depth information."
                  },
                  {
                    icon: "🔍",
                    title: "Deep market insights",
                    description: "Advanced analytics and market sentiment indicators."
                  },
                  {
                    icon: "📱",
                    title: "Access via web and mobile app",
                    description: "Trade anywhere with our responsive web and mobile platforms."
                  },
                  {
                    icon: "⭐",
                    title: "Customizable watchlists",
                    description: "Create personalized watchlists to monitor your favorite assets."
                  }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-xl flex-shrink-0 shadow-lg">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="text-green-600 font-semibold hover:text-green-700 transition-colors flex items-center">
                Learn more
                <ChevronRightIcon className="w-5 h-5 ml-2" />
              </button>
            </div>
            <div className="relative">
              <div className="w-64 h-64 bg-black rounded-full flex items-center justify-center mx-auto">
                <div className="text-white text-6xl">🔍</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crypto Futures Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img src={futuresImage} alt="Crypto Futures" className="w-full max-w-md mx-auto" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
                Explore crypto futures
              </h2>
              <div className="space-y-6 mb-8">
                {[
                  {
                    icon: "⚙️",
                    title: "Flexible contract sizes",
                    description: "Trade with contract sizes that match your risk tolerance."
                  },
                  {
                    icon: "📈",
                    title: "Advanced charting tools",
                    description: "Professional-grade charting with technical indicators."
                  },
                  {
                    icon: "💎",
                    title: "Portfolio hedging",
                    description: "Use futures to hedge your spot positions and manage risk."
                  }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-xl flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="text-green-600 font-semibold hover:text-green-700 transition-colors flex items-center">
                Learn more
                <ChevronRightIcon className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tax Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
                Simplify your crypto taxes with maximum precision
              </h2>
              <div className="space-y-6 mb-8">
                {[
                  {
                    icon: "📄",
                    title: "Integrated tax reporting",
                    description: "Automatically generate comprehensive tax reports for all your trading activity."
                  },
                  {
                    icon: "✅",
                    title: "Automated gain/loss calculations",
                    description: "Accurate calculation of capital gains and losses across all transactions."
                  },
                  {
                    icon: "🏢",
                    title: "Support for multiple jurisdictions",
                    description: "Tax reporting compliant with regulations in your country."
                  }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-xl flex-shrink-0 shadow-lg">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="text-green-600 font-semibold hover:text-green-700 transition-colors flex items-center">
                Learn more
                <ChevronRightIcon className="w-5 h-5 ml-2" />
              </button>
            </div>
            <div>
              <img src={retirementImage} alt="Tax Tool" className="w-full max-w-md mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-16">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-lg"
              >
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors rounded-lg"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  {openFaq === index ? (
                    <MinusIcon className="w-5 h-5 text-gray-500" />
                  ) : (
                    <PlusIcon className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExchangePage; 